import { useEffect, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { ApiError } from '../../api/index.js';

export function ResetPasswordModal({ open, user, onClose, onConfirm }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPassword('');
    setConfirm('');
    setBusy(false);
    setError(null);
  }, [open]);

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= 12 && password.length <= 128 && password === confirm && !busy;

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(password);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.payload?.error || err.message : err?.message || 'Operation failed.',
      );
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={`Reset password — ${user?.login || ''}`}
      description="The user will be signed out from all sessions and must use the new password."
      size="sm"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-secondary" disabled={busy} data-skip-autofocus>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="btn-primary">
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Working…
              </>
            ) : (
              <>
                <i className="fa-solid fa-key" aria-hidden="true" /> Reset password
              </>
            )}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}
        <div>
          <label htmlFor="newpw" className="field-label">New password</label>
          <input
            id="newpw"
            type="password"
            className="field"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Min. 12 characters"
            autoComplete="new-password"
            disabled={busy}
          />
          {tooShort && <p className="mt-1 text-xs text-rose-600">Password must be at least 12 characters.</p>}
        </div>
        <div>
          <label htmlFor="confpw" className="field-label">Confirm new password</label>
          <input
            id="confpw"
            type="password"
            className="field"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repeat password"
            autoComplete="new-password"
            disabled={busy}
          />
          {mismatch && <p className="mt-1 text-xs text-rose-600">Passwords do not match.</p>}
        </div>
      </form>
    </Modal>
  );
}
